/**
 * Positions Catalog Hook
 * Supports filtering by areaId - positions reload when areaId changes
 */

import { useState, useEffect } from 'react';
import { positionsCatalogService } from '../positions.service.ts';
import type { Position } from '../positions.types.ts';


interface UsePositionsResult {
  positions: Position[];
  loading: boolean;
  error: string | null;
}

export function usePositions(areaId?: number | null): UsePositionsResult {
  const [positions, setPositions] = useState<Position[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    void (async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await positionsCatalogService.getPositions(
          areaId != null ? String(areaId) : undefined,
        );

        if (!isMounted) return;

        if (response.success && response.data) {
          setPositions(response.data);
        } else if (response.error) {
          setError(response.error.description || 'Error loading positions');
        }
      } catch (catchError) {
        if (!isMounted) return;
        setError(catchError instanceof Error ? catchError.message : 'Unknown error');
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [areaId]);

  return { positions, loading, error };
}
